import { MoreVertical, Pencil } from 'lucide-react'
import React from 'react'
import { useTranslation } from 'react-i18next'

import OptionButton from '@/components-latest/primitives/OptionButton'
import {
  PopoverContent,
  PopoverTrigger
} from '@/components-latest/primitives/Popover'
import Popover from '@/components-latest/primitives/Popover/Popover'
import Text from '@/components-latest/primitives/Text'
import DisableConfirmation from '@/components-latest/ui/OfferPage/DisableConfirmation'
import type { Campaign } from '@/ducks/campaigns/campaigns.types'
import type { FetchCampaignsQueryParams } from '@/ducks/campaigns/api/campaigns.api'
import useCampaignActions from '@/components-latest/ui/CampaignsPage/useCampaignActions'
import { useToggle } from '@/hooks/useToggle/useToggle'

import Styles from './CampaignDetailsModal.module.scss'

type Props = {
  campaign: Campaign
  onClose: () => void
  campaignsQueryParams?: FetchCampaignsQueryParams
}

const CampaignDetailsModalFooter = ({
  campaign,
  onClose,
  campaignsQueryParams
}: Props) => {
  const { t } = useTranslation()
  const [isMenuOpen, toggleMenuOpen] = useToggle(false)

  const {
    handleEdit,
    handleDeactivate,
    handleDelete,
    isExpired,
    isDeactivateDisabled,
    deactivateLabel,
    deactivateDescription,
    deleteTitle,
    deleteDescription,
    isDeleteOpen,
    setIsDeleteOpen,
    isDeactivateOpen,
    setIsDeactivateOpen
  } = useCampaignActions(campaign, {
    onActionComplete: onClose,
    campaignsQueryParams
  })

  const closeMenu = () => {
    if (isMenuOpen) toggleMenuOpen()
  }

  return (
    <>
      <div className={Styles.footerActions}>
        <Popover open={isMenuOpen} onOpenChange={toggleMenuOpen}>
          <PopoverTrigger asChild>
            <button
              type="button"
              className={Styles.iconActionButton}
              aria-label={t('marketing.campaigns.more-actions')}
              data-testid="campaign-details-more-actions">
              <MoreVertical width={20} height={20} />
            </button>
          </PopoverTrigger>
          <PopoverContent align="end" className={Styles.footerPopover}>
            <OptionButton
              className={Styles.footerOption}
              disabled={isDeactivateDisabled}
              onClick={() => {
                closeMenu()
                setIsDeactivateOpen(true)
              }}>
              <Text variant="nextSubtitle">{deactivateLabel}</Text>
            </OptionButton>
            <OptionButton
              className={Styles.footerOption}
              onClick={() => {
                closeMenu()
                setIsDeleteOpen(true)
              }}>
              <Text variant="nextSubtitle">{t('offers.delete-offer')}</Text>
            </OptionButton>
          </PopoverContent>
        </Popover>

        <button
          type="button"
          className={Styles.editButton}
          disabled={isExpired}
          onClick={handleEdit}>
          <Pencil width={16} height={16} className={Styles.editButtonIcon} />
          <span className={Styles.editButtonLabel}>
            {t('marketing.campaigns.details.edit')}
          </span>
        </button>
      </div>

      <DisableConfirmation
        open={isDeactivateOpen}
        onOpenChange={setIsDeactivateOpen}
        onConfirm={handleDeactivate}
        title={deactivateLabel}
        description={deactivateDescription}
        cancelText={t('common.modal.cancel')}
        confirmText={t('common.modal.confirm')}
      />

      <DisableConfirmation
        open={isDeleteOpen}
        onOpenChange={setIsDeleteOpen}
        onConfirm={handleDelete}
        title={deleteTitle}
        description={deleteDescription}
        cancelText={t('common.modal.cancel')}
        confirmText={t('common.modal.confirm')}
      />
    </>
  )
}

export default CampaignDetailsModalFooter
